import * as ko from "knockout";
import type { Observable } from "knockout";
import * as _ from "lodash";

import * as Backend from "../../backend/v1/sdk.gen";
import type { CageHardwareStatusResponse, TecniplastUnknownCageLocation } from "../../backend/v1/types.gen";
import { translated } from "../../lib/localize";
import { naturalCompare } from "../../lib/utils";
import type { FetchBackendExtended } from "../extensions/fetchBackend";

import template from "./cageHardwareWidget.html";

import "./cageHardwareWidget.scss";
import { handleBackendError } from "../../backend/v1/utils";
import type { HtmlDialog } from "../../lib/popups";
import { notifications } from "../../pyratFrontend";
import { htmlDialogStarter } from "../dialogStarter";

interface CageHardwareWidgetParams {
    cageNumber: string; // CageNumber of the cage.
    cageId: number; // Database ID of the cage.
}

const showConnectCage = htmlDialogStarter(
    class {
        connectCagePositions = ko.observable<TecniplastUnknownCageLocation[]>([]);
        fetchInProgress = ko.observable(false);

        refresh = () =>
            this.fetchInProgress(true) &&
            Backend.getUnknownCages()
                .then(({ data }) => {
                    this.connectCagePositions(data);
                })
                .finally(() => this.fetchInProgress(false));

        constructor(
            readonly dialog: HtmlDialog,
            readonly params: {
                cageId: number;
                cageNumber: string;
                eventTarget: HTMLElement;
                readonly setConnectCageInProgress: (value: boolean) => any;
                onSubmit: () => any;
            },
        ) {
            this.refresh();
        }

        public collectPositionGroups = (positions: TecniplastUnknownCageLocation["positions"]) => {
            const columns = _.uniq(
                _.map(positions, (p) => {
                    return p.slice(1);
                }),
            )
                .sort(naturalCompare)
                .reverse();
            const rows = _.uniq(
                _.map(positions, (p) => {
                    return p.slice(0, 1);
                }),
            ).sort(naturalCompare);

            return _.map(columns, (c) => {
                return _.map(rows, (r) => {
                    if (_.includes(positions, r + c)) return r + c;
                    return undefined;
                });
            });
        };

        public connectCage = (location: TecniplastUnknownCageLocation, position: string) => {
            this.params.setConnectCageInProgress(true);
            this.dialog.close();
            Backend.connectCage({
                path: {
                    cage_id: this.params.cageId,
                },
                body: {
                    rack_id: location.amsRackId,
                    position: position,
                },
            })
                .then(() => {
                    notifications.showNotification(
                        _.template(translated("Connected cage <%- cageNumber %>."))({
                            cageNumber: this.params.cageNumber,
                        }),
                        "success",
                    );
                })
                .catch(
                    handleBackendError({
                        message: _.template(translated("Connecting cage <%- cageNumber %> failed."))({
                            cageNumber: this.params.cageNumber,
                        }),
                    }),
                )
                .finally(() => {
                    this.params.setConnectCageInProgress(false);
                    this.params.onSubmit();
                });
        };
    },
    `
    <div class="connect_tecniplast_dvc_cage_popup">

        <div class="tw:flex">
            <span class=""
                   data-bind="text: translated('Select an empty / unassigned cage position:')"></span>
            <span class="two_bubbles_loader_container"
                  style="position: relative"
                  data-bind="visible: fetchInProgress">
                  <span class="two_bubbles_loader"></span>
            </span>
            <button type="button"
                    class="tw:text-nowrap"
                    data-bind="disable:fetchInProgress,
                               text: translated('Reload'),
                               click: refresh"></button>
        </div>

        <div class="locations">
            <!-- ko foreach: {data: _.sortBy(connectCagePositions(), 'rackName'), as: 'location' } -->
                <div class="possible_location">
                    <div class="location">
                        <span data-bind="text: location.rackName"></span>
                    </div>
                    <table>
                        <tbody data-bind="foreach: {data: $root.collectPositionGroups(location.positions), as: 'position_group' }">
                            <tr data-bind="foreach: {data: position_group, as: 'position' }">
                                <td data-bind="if: position">
                                    <a class="subtle-link"
                                       data-bind="text: position,
                                                  click: () => $root.connectCage(location, position)"></a></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            <!-- /ko -->
            <!-- ko if: !connectCagePositions() || connectCagePositions().length < 1 -->
                <div class="no_possible_location" data-bind="text: translated('No possible locations found.')"></div>
            <!-- /ko -->
        </div>

    </div>
    `,
    (params) => ({
        name: "ConnectTecniplastDvcCagePopup",
        title: _.template(translated("Connect <%- cageNumber %> with Tecniplast DVC"))({
            cageNumber: params.cageNumber,
        }),
        width: 450,
        height: "auto",
        position: { anchor: params.eventTarget },
    }),
);

class TecniplastModel {
    public widget: CageHardwareWidgetViewModel;
    public cage: CageHardwareStatusResponse["provider"]["tecniplast"];
    public obtainTecniplastDVCPositionInProgress: Observable<boolean>;
    public updateTecniplastDVCCageInProgress: Observable<boolean>;
    public disconnectCageInProgress: Observable<boolean>;
    public connectCageInProgress: Observable<boolean>;
    public turnLabelLEDOnInProgress: Observable<boolean>;
    public turnLabelLEDOffInProgress: Observable<boolean>;

    constructor(widget: CageHardwareWidgetViewModel, seed: CageHardwareStatusResponse["provider"]["tecniplast"]) {
        this.widget = widget;
        this.cage = seed;

        this.obtainTecniplastDVCPositionInProgress = ko.observable(false);
        this.updateTecniplastDVCCageInProgress = ko.observable(false);
        this.disconnectCageInProgress = ko.observable(false);
        this.connectCageInProgress = ko.observable(false);
        this.turnLabelLEDOnInProgress = ko.observable(false);
        this.turnLabelLEDOffInProgress = ko.observable(false);
    }

    public connectCageShow = (item: CageHardwareWidgetViewModel, event: MouseEvent) => {
        const target = event.target as HTMLElement;
        showConnectCage({
            cageId: this.widget.cageId,
            cageNumber: this.widget.cageNumber,
            eventTarget: target,
            setConnectCageInProgress: this.connectCageInProgress,
            onSubmit: this.widget.seed.forceReload,
        });
    };

    public obtainTecniplastDVCPosition = () => {
        this.obtainTecniplastDVCPositionInProgress(true);
        Backend.obtainDvcPosition({
            path: {
                cage_id: this.widget.cageId,
            },
        })
            .then(() => {
                notifications.showNotification(
                    _.template(translated("Obtained the position of cage <%- cageNumber %>."))({
                        cageNumber: this.widget.cageNumber,
                    }),
                    "success",
                );
            })
            .catch(() => {
                notifications.showNotification(
                    _.template(translated("Obtaining the position of cage <%- cageNumber %> failed."))({
                        cageNumber: this.widget.cageNumber,
                    }),
                    "error",
                );
            })
            .finally(() => {
                this.obtainTecniplastDVCPositionInProgress(false);
                this.widget.seed.forceReload();
            });
    };

    public updateTecniplastDVCCage = () => {
        this.updateTecniplastDVCCageInProgress(true);
        Backend.updateDvcCage({
            path: {
                cage_id: this.widget.cageId,
            },
        })
            .then(() => {
                notifications.showNotification(
                    _.template(translated("Updated cage <%- cageNumber %>."))({
                        cageNumber: this.widget.cageNumber,
                    }),
                    "success",
                );
            })
            .catch(() => {
                notifications.showNotification(
                    _.template(translated("Updating cage <%- cageNumber %> failed."))({
                        cageNumber: this.widget.cageNumber,
                    }),
                    "error",
                );
            })
            .finally(() => {
                this.updateTecniplastDVCCageInProgress(false);
            });
    };

    public disconnectCage = () => {
        this.disconnectCageInProgress(true);
        Backend.disconnectCage({
            path: {
                cage_id: this.widget.cageId,
            },
        })
            .then(() => {
                notifications.showNotification(
                    _.template(translated("Disconnected cage <%- cageNumber %>."))({
                        cageNumber: this.widget.cageNumber,
                    }),
                    "success",
                );
            })
            .catch(() => {
                notifications.showNotification(
                    _.template(translated("Disconnecting cage <%- cageNumber %> failed."))({
                        cageNumber: this.widget.cageNumber,
                    }),
                    "error",
                );
            })
            .finally(() => {
                this.disconnectCageInProgress(false);
                this.widget.seed.forceReload();
            });
    };

    public turnLabelLEDOn = () => {
        this.turnLabelLEDOnInProgress(true);
        Backend.turnCageLedOn({
            path: {
                cage_id: this.widget.cageId,
            },
        })
            .then(() => {
                this.widget.seed.forceReload();
            })
            .finally(() => {
                this.turnLabelLEDOnInProgress(false);
            });
    };

    public turnLabelLEDOff = () => {
        this.turnLabelLEDOffInProgress(true);
        Backend.turnCageLedOff({
            path: {
                cage_id: this.widget.cageId,
            },
        })
            .then(() => {
                this.widget.seed.forceReload();
            })
            .finally(() => {
                this.turnLabelLEDOffInProgress(false);
            });
    };
}

class GalileiModel {
    public widget: CageHardwareWidgetViewModel;
    public cage: CageHardwareStatusResponse["provider"]["galilei"];
    public freeLabelId: Observable<string>;
    public pairLabelInProgress: Observable<boolean>;
    public unpairLabelInProgress: Observable<boolean>;
    public turnLabelLEDOnInProgress: Observable<boolean>;
    public turnLabelLEDOffInProgress: Observable<boolean>;
    public updateLabelInProgress: Observable<boolean>;

    constructor(widget: CageHardwareWidgetViewModel, seed: CageHardwareStatusResponse["provider"]["galilei"]) {
        this.widget = widget;
        this.cage = seed;
        this.freeLabelId = ko.observable("");
        this.pairLabelInProgress = ko.observable(false);
        this.unpairLabelInProgress = ko.observable(false);
        this.turnLabelLEDOnInProgress = ko.observable(false);
        this.turnLabelLEDOffInProgress = ko.observable(false);
        this.updateLabelInProgress = ko.observable(false);
    }

    public pairLabel = () => {
        this.pairLabelInProgress(true);
        Backend.pairLabel({
            path: {
                cage_id: this.widget.cageId,
            },

            body: { label_id: this.freeLabelId() },
        })
            .then(() => {
                const message = translated("Paired cage <%- cageNumber %>.");
                notifications.showNotification(_.template(message)({ cageNumber: this.widget.cageNumber }), "success");
            })
            .catch(() => {
                const message = translated("Pairing cage <%- cageNumber %> failed.");
                notifications.showNotification(_.template(message)({ cageNumber: this.widget.cageNumber }), "error");
            })
            .finally(() => {
                this.widget.seed.forceReload();
                this.pairLabelInProgress(false);
            });
    };

    public unpairLabel = () => {
        this.unpairLabelInProgress(true);
        Backend.unpairLabel({
            path: {
                cage_id: this.widget.cageId,
            },
        })
            .then(() => {
                const message = translated("Unpaired cage <%- cageNumber %>.");
                notifications.showNotification(_.template(message)({ cageNumber: this.widget.cageNumber }), "success");
            })
            .catch(() => {
                const message = translated("Unpairing cage <%- cageNumber %> failed.");
                notifications.showNotification(_.template(message)({ cageNumber: this.widget.cageNumber }), "error");
            })
            .finally(() => {
                this.widget.seed.forceReload();
                this.unpairLabelInProgress(false);
            });
    };

    public turnLabelLEDOn = () => {
        this.turnLabelLEDOnInProgress(true);
        Backend.turnLabelLedOn({
            path: {
                cage_id: this.widget.cageId,
            },
        })
            .then(() => {
                this.widget.seed.forceReload();
            })
            .finally(() => {
                this.turnLabelLEDOnInProgress(false);
            });
    };

    public turnLabelLEDOff = () => {
        this.turnLabelLEDOffInProgress(true);
        Backend.turnLabelLedOff({
            path: {
                cage_id: this.widget.cageId,
            },
        })
            .then(() => {
                this.widget.seed.forceReload();
            })
            .finally(() => {
                this.turnLabelLEDOffInProgress(false);
            });
    };

    public updateLabel = () => {
        this.updateLabelInProgress(true);
        Backend.updateLabel({
            path: {
                cage_id: this.widget.cageId,
            },
        }).finally(() => {
            this.updateLabelInProgress(false);
        });
    };
}

class CageHardwareWidgetViewModel {
    public cageNumber: string;
    public cageId: number;
    public error: Observable<string | undefined>;
    public tecniplast: Observable<TecniplastModel | undefined>;
    public galilei: Observable<GalileiModel | undefined>;
    public seed: FetchBackendExtended<Observable<CageHardwareStatusResponse> | undefined>;

    constructor({ cageNumber, cageId }: CageHardwareWidgetParams) {
        this.cageNumber = cageNumber;
        this.cageId = cageId;

        this.error = ko.observable();
        this.seed = ko.observable().extend({
            fetchBackend: () =>
                Backend.getCageHardwareStatus({
                    query: {
                        cage_id: cageId,
                    },
                }),
        });

        this.seed.onCatch = (error) => {
            if (error?.error?.detail) {
                this.error(error.error.detail);
            } else {
                this.error(translated("An error occurred while loading the cage hardware status."));
            }
        };

        // initialize the configured hardware models
        this.tecniplast = ko.observable(undefined);
        this.galilei = ko.observable(undefined);
        this.seed.subscribe((seed) => {
            // reset error
            this.error(undefined);

            // tecniplast dvc
            if (seed?.provider?.tecniplast) {
                this.tecniplast(new TecniplastModel(this, seed.provider.tecniplast));
            } else {
                this.tecniplast(undefined);
            }

            // galilei cage talker
            if (seed?.provider?.galilei) {
                this.galilei(new GalileiModel(this, seed.provider.galilei));
            } else {
                this.galilei(undefined);
            }
        });
    }
}

export class CageHardwareWidgetComponent {
    constructor() {
        // biome-ignore lint/correctness/noConstructorReturn:
        return {
            viewModel: CageHardwareWidgetViewModel,
            template,
        };
    }
}
